/**
 * Scroll reveal.
 *
 * Replaces a hand-rolled AOS clone that measured every [data-aos] element with
 * getBoundingClientRect() on a throttled scroll listener *and* on every mutation of the
 * whole document (childList + subtree on documentElement). Any DOM change anywhere — a
 * lazyloaded image, a carousel building itself, an app injecting a node — re-measured every
 * element on the page. That was the largest forced reflow in the document.
 *
 * IntersectionObserver gives identical behaviour without ever reading layout from script:
 * elements fade in over 400ms once their top comes within 120px of the viewport bottom, and
 * stay visible afterwards.
 */
(() => {
  // Matched the previous settings.offset. The reveal fires slightly *before* the element
  // reaches the fold, so a positive bottom rootMargin grows the intersection area downwards.
  const OFFSET = 120;
  const SELECTOR = '[data-aos]';

  const root = document.documentElement;

  // Tells the inline guard in layout/theme.liquid that this file made it; without this the
  // guard disarms the reveal so nothing is left stuck at opacity: 0.
  window.__aosReady = true;

  const showEverything = () => root.classList.remove('aos-enabled');

  if (!('IntersectionObserver' in window)) {
    showEverything();
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('aos-animate');
        observer.unobserve(entry.target);      // the old config was once: true
      });
    },
    { rootMargin: `0px 0px ${OFFSET}px 0px`, threshold: 0 }
  );

  const observe = (scope) => {
    const el = scope && scope.querySelectorAll ? scope : document;
    el.querySelectorAll(`${SELECTOR}:not(.aos-animate):not([data-aos-observed])`).forEach((node) => {
      node.setAttribute('data-aos-observed', '');
      observer.observe(node);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => observe(document));
  } else {
    observe(document);
  }

  // The theme editor re-renders a section on every setting change. Scoped to the section that
  // changed rather than watching the whole document, which is what made the old version slow.
  document.addEventListener('shopify:section:load', (event) => observe(event.target));

  // Kept for anything that used to call the old global.
  window.AOS = { refresh: () => observe(document), disable: showEverything };
})();
